import { Box, Grid, useTheme } from '@achieve/ascend'
import { Typography } from 'components/Typography'
import { useReducer } from 'react'
import {
  styled,
  ClickAwayListener,

  // NOTE - This should eventually be replaced with the Ascend version. As of 8/9/2023, the MUI
  // Tooltip from Ascend doesn't seem to support `TooltipProps` and even more importantly
  // `tooltipClasses` which are needed to adjust styles of the tooltip `popper`. Using the version
  // from MUI for the time being to move forward.
  tooltipClasses,
  Tooltip,
} from '@mui/material'
import { Info, X } from 'react-feather'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'

const reducer = (state, action) => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return { ...state, modal: true }
    case 'CLOSE_MODAL':
      return { ...state, modal: false }
    default:
      return state
  }
}

export const ControlledTooltip = ({
  iconSize = 16,
  width,
  modalTitle = undefined,
  modalBody = undefined,
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, { modal: false })
  const { modal } = state
  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)
  if (width === undefined) width = isMobile ? 280 : 400

  const handleTooltipClose = () => {
    dispatch({ type: 'CLOSE_MODAL' })
  }

  const handleTooltipOpen = () => {
    dispatch({ type: 'OPEN_MODAL' })
  }

  const getContent = () => {
    return (
      <Grid
        container
        wrap="nowrap"
        direction="row-reverse"
        justifyContent="space-between"
        alignItems="start"
      >
        <Grid item>
          <X
            onClick={handleTooltipClose}
            className="text-[#C0CBD8] cursor-pointer h-6 w-6 -translate-y-2 hover:text-[#C0CBD8]"
          />
        </Grid>
        <Grid item>
          {modalTitle && (
            <Box component="span" display={'flex'}>
              {modalTitle}
            </Box>
          )}
          {modalBody && <Box component="span">{modalBody}</Box>}
        </Grid>
      </Grid>
    )
  }

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <span>
        <HtmlTooltip
          title={getContent()}
          placement="top"
          onClose={handleTooltipClose}
          open={modal}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          onClick={handleTooltipOpen}
          width={width}
        >
          <div>
            <Info
              className="cursor-pointer inline-block text-inherit ml-2 align-middle hover:text-[#5C708A]"
              style={{
                width: iconSize,
                height: iconSize,
              }}
            />
            {children}
          </div>
        </HtmlTooltip>
      </span>
    </ClickAwayListener>
  )
}

// Styled wrapper for Ascend Tooltip
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} placement="bottom" classes={{ popper: className }} />
))(({ theme, width }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: `${theme.ascend.colors.neutral[0]}`,
    border: `1px solid ${theme.ascend.colors.neutral[150]}`,
    borderRadius: 10,
    maxWidth: width,
    color: `${theme.ascend.colors.neutral[975]}`,
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
}))

export const ControlledTooltipTitle = ({ children }) => {
  const theme = useTheme()

  return (
    <Typography variant="bodyMd" fontWeight="bold" sx={{ marginBottom: theme.spacing(0.5) }}>
      {children}
    </Typography>
  )
}

export const ControlledTooltipBody = ({ children }) => {
  const theme = useTheme()
  return (
    <Typography component="div" variant="bodySm" color={theme.ascend.colors.neutral[975]}>
      {children}
    </Typography>
  )
}
